import axios, {
  AxiosResponse,
  CreateAxiosDefaults,
  HttpStatusCode,
} from 'axios';

import { useTokensStore } from '../../stores/useTokensStore';
import { RefreshTokenRequest } from '../../requests/RefreshTokenRequet';
import { AccessTokensModel } from '../../models/AccessTokensModel';

export type CreateAxiosClientOptions = {
  axiosOptions: CreateAxiosDefaults;
  refreshTokenUrl: string;
};
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
/**
 * Creates a secured Axios client with token injection and token refresh functionality.
 * @param tokenStorage - The token storage object used to read and write access tokens and refresh tokens.
 * @param options - The options object containing Axios configuration options and refresh token URL.
 * @returns The secured Axios client.
 */

export const createSecuredAxiosClient = (options: CreateAxiosClientOptions) => {
  const axiosClient = axios.create(options.axiosOptions);

  // Inject token for each request
  axiosClient.interceptors.request.use(
    async (config) => {
      const accessToken = useTokensStore.getState().accessToken;

      if (!accessToken) {
        throw new Error('No access token found');
      }

      config.headers.Authorization = `Bearer ${accessToken}`;

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { response, config } = error;

      if (!response || !config) {
        return Promise.reject(error);
      }

      /**
       * 2️⃣ LOG OUT IMMEDIATELY ON 401
       */
      if (response.status === HttpStatusCode.Unauthorized) {
        useTokensStore.getState().clear();
        window.location.replace('/');

        return Promise.reject(error);
      }

      /**
       * Only handle 403 here
       */
      if (response.status !== HttpStatusCode.Forbidden) {
        return Promise.reject(error);
      }

      /**
       * Prevent infinite retry loop
       */
      if (config._retry) {
        window.location.replace('/');

        return Promise.reject(error);
      }

      config._retry = true;

      const { refreshToken, refreshTokenExpiration } =
        useTokensStore.getState();

      if (!refreshToken || !options.refreshTokenUrl) {
        useTokensStore.getState().clear();
        window.location.replace('/');

        return Promise.reject(error);
      }

      /**
       * 1️⃣ WAIT FOR EXISTING REFRESH
       */
      if (isRefreshing && refreshPromise) {
        await refreshPromise;

        return axiosClient(config);
      }

      /**
       * START REFRESH
       */
      isRefreshing = true;

      // DO NOT USE THE SAME AXIOS INSTANCE HERE!
      refreshPromise = axios
        .post<
          AccessTokensModel,
          AxiosResponse<AccessTokensModel>,
          RefreshTokenRequest
        >(options.refreshTokenUrl, {
          refreshToken,
        })
        .then(({ data }) => {
          useTokensStore.setState({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken || refreshToken,
            accessTokenExpiration: data.accessTokenExpiration,
            refreshTokenExpiration:
              data.refreshTokenExpiration || refreshTokenExpiration,
          });
        })
        .catch((err) => {
          useTokensStore.getState().clear();
          window.location.replace('/');

          throw err;
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });

      await refreshPromise;

      return axiosClient(config);
    }
  );

  return axiosClient;
};
